import React from 'react'
import PropTypes from 'prop-types'
import {
  Button,
  Form,
  Heading,
  Level,
  Box,
  Checkbox,
  Collapse,
  Text,
} from '@deseretbook/react-ui'
import {
  AddressFormManager,
  AddressFormFields,
  PaymentFormFields,
  PaymentFormManager,
} from '../../forms'

/**
 * Form to create a payment method in the checkout process.
 *
 * @returns {function} Component
 */
const PaymentMethodForm = ({
  address,
  onChange,
  onSubmit,
  isSubmitting,
  onCancel,
  saveButtonText,
  paymentMethod,
}) => {
  const [useDeliveryAddress, setUseDeliveryAddress] = React.useState(true)

  return (
    <AddressFormManager address={address} onChange={onChange}>
      {addressFormProps => (
        <PaymentFormManager paymentMethod={paymentMethod}>
          {(paymentFormProps) => {
            const { hostedFields } = paymentFormProps
            const isFetchingData = addressFormProps.isFetchingData
              || paymentFormProps.isFetchingData

            // We pass the entire `hostedFields` to the `onSubmit` because the `tokenize`
            // won't work if we send it by itself because apparently it inherits
            // the `this` of the component and not its original `this`.
            // We also pass it in instead of calling `tokenize` separately because
            // we want the parent component to deal with form control.
            return (
              <Form
                onSubmit={() => onSubmit({ hostedFields, useDeliveryAddress })}
                isSubmitting={isSubmitting}
              >
                <PaymentFormFields {...paymentFormProps} />
                <Box paddingVertical={1}>
                  <Heading>Billing Address</Heading>
                </Box>
                <Checkbox
                  checked={useDeliveryAddress}
                  onChange={({ target }) => setUseDeliveryAddress(target.checked)}
                >
                  <Text>Use delivery address</Text>
                </Checkbox>
                <Collapse isCollapsed={useDeliveryAddress}>
                  {!useDeliveryAddress && <AddressFormFields {...addressFormProps} />}
                </Collapse>

                <Level>
                  <Level.Right>
                    {!!onCancel && (
                      <Level.Item>
                        <Button
                          type="button"
                          disabled={isFetchingData || isSubmitting}
                          onClick={onCancel}
                        >
                          Cancel
                        </Button>
                      </Level.Item>
                    )}
                    <Level.Item>
                      <Form.SubmitButton
                        color="action"
                        disabled={isFetchingData}
                      >
                        {saveButtonText}
                      </Form.SubmitButton>
                    </Level.Item>
                  </Level.Right>
                </Level>
              </Form>
            )
          }}
        </PaymentFormManager>
      )}
    </AddressFormManager>
  )
}

PaymentMethodForm.propTypes = {
  // Address data.
  address: PropTypes.shape({
    address1: PropTypes.string,
    address2: PropTypes.string,
    address3: PropTypes.string,
    city: PropTypes.string,
    countryId: PropTypes.number,
    firstname: PropTypes.string,
    lastname: PropTypes.string,
    phone: PropTypes.string,
    stateId: PropTypes.number,
    stateName: PropTypes.string,
    zipcode: PropTypes.string,
  }),
  // Text to display inside the submit button.
  saveButtonText: PropTypes.string,
  // callback for when the address changes.
  onChange: PropTypes.func,
  // callback for when the form should be cancelled.
  onCancel: PropTypes.func,
  // Callback for when the form is to be submitted.
  onSubmit: PropTypes.func,
  // If the onSubmit callback is currently running.
  isSubmitting: PropTypes.bool,
  /** payment method to use */
  paymentMethod: PropTypes.shape({}),
}

PaymentMethodForm.defaultProps = {
  address: {},
  saveButtonText: 'Save Payment',
  onChange: () => {},
  onCancel: null,
  onSubmit: () => {},
  isSubmitting: false,
  paymentMethod: null,
}

export default PaymentMethodForm
